import React from 'react';
import {
  Container,
  Header,
  SpaceBetween,
  FormField,
} from '@cloudscape-design/components';
import RequiredFieldLabel from '../../RequiredFieldLabel';
import { QuestionFormParams } from '../QuestionFormWizard';
import TextEditor, { FileData } from '../../textEditor/TextEditor';
import { FileMetadata } from '../../FileDisplay';
import { AuditTooltipMessages, MARKDOWN_TEXT_WARNING } from 'src/contstant/commonLabels';
import TaskForm, { TaskData } from '../TaskForm';
import Tooltip from 'src/components/Tooltip';

interface AssessmentCriteriaStepProps {
  formData: QuestionFormParams;
  setFormData: React.Dispatch<React.SetStateAction<QuestionFormParams>>;
  fieldValidationError: any;
  fileList: Record<string, FileData[]>;
  setFileList: React.Dispatch<React.SetStateAction<Record<string, FileData[]>>>;
  documentFiles?: Record<string, File[]>;
  setDocumentFiles?: React.Dispatch<React.SetStateAction<Record<string, File[]>>>;
  documentFilesMetadata?: Record<string, FileMetadata[]>;
  setDocumentFilesMetadata?: React.Dispatch<
    React.SetStateAction<Record<string, FileMetadata[]>>
  >;
  enableFileUploads?: boolean;
}

const AuditCriteria: React.FC<AssessmentCriteriaStepProps> = ({
  formData,
  setFormData,
  fieldValidationError,
  fileList,
  setFileList,
  documentFiles = {},
  setDocumentFiles,
  documentFilesMetadata = {},
  setDocumentFilesMetadata,
  enableFileUploads = true,
}) => {
  const taskData: TaskData = {
    taskName: formData.taskName || '',
    taskDescription: formData.taskDescription || '',
    taskDurationDays: formData.taskDurationDays || 0,
  };

  return (
    <div>
      <SpaceBetween size="l">
        <FormField
          data-testid="question-text-field"
          stretch
          label={<RequiredFieldLabel label="Question Text" />}
          errorText={fieldValidationError.questionText}
          description={MARKDOWN_TEXT_WARNING}
          info={<Tooltip content={AuditTooltipMessages.QUESTION_TEXT} />}
        >
          {' '}
          <Container>
            <TextEditor
              data-testid="question-text-editor"
              placeholder="Enter question text..."
              value={formData.questionText}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  questionText: value,
                }))
              }
              onChangeMedia={(data) =>
                setFileList((prev) => ({ ...prev, questionText: data }))
              }
              onFilesChange={(data) => {
                setDocumentFiles?.((prev) => ({ ...prev, questionText: data.files }));
                setDocumentFilesMetadata?.((prev) => ({
                  ...prev,
                  questionText: data.metadata,
                }));
              }}
              showFileUpload={enableFileUploads}
              initialFileMetadata={documentFilesMetadata.questionText || []}
            />
          </Container>
        </FormField>

        <FormField
          data-testid="audit-instructions-field"
          stretch
          label={<RequiredFieldLabel label="Audit Instructions" />}
          errorText={fieldValidationError.auditInstruction}
          description={MARKDOWN_TEXT_WARNING}
          info={<Tooltip content={AuditTooltipMessages.AUDIT_INSTRUCTION} />}
        >
          <Container>
            <TextEditor
              data-testid="audit-instructions-editor"
              placeholder="Enter audit instruction details..."
              value={formData.auditInstruction}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  auditInstruction: value,
                }))
              }
              onChangeMedia={(data) =>
                setFileList((prev) => ({ ...prev, auditInstruction: data }))
              }
              onFilesChange={(data) => {
                setDocumentFiles?.((prev) => ({ ...prev, auditInstruction: data.files }));
                setDocumentFilesMetadata?.((prev) => ({
                  ...prev,
                  auditInstruction: data.metadata,
                }));
              }}
              showFileUpload={enableFileUploads}
              initialFileMetadata={documentFilesMetadata.auditInstruction || []}
            />
          </Container>
        </FormField>

        <FormField
          data-testid="pass-criteria-field"
          label={<RequiredFieldLabel label="Pass Criteria" />}
          stretch
          errorText={fieldValidationError.passCriteria}
          description={MARKDOWN_TEXT_WARNING}
          info={<Tooltip content={AuditTooltipMessages.PASS_CRITERIA} />}
        >
          <Container>
            <TextEditor
              data-testid="pass-criteria-editor"
              placeholder="Enter pass criteria details..."
              value={formData.passCriteria}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  passCriteria: value,
                }))
              }
              onChangeMedia={(data) =>
                setFileList((prev) => ({ ...prev, passCriteria: data }))
              }
              onFilesChange={(data) => {
                setDocumentFiles?.((prev) => ({ ...prev, passCriteria: data.files }));
                setDocumentFilesMetadata?.((prev) => ({
                  ...prev,
                  passCriteria: data.metadata,
                }));
              }}
              showFileUpload={enableFileUploads}
              initialFileMetadata={documentFilesMetadata.passCriteria || []}
            />
          </Container>
        </FormField>

        <FormField
          data-testid="fail-criteria-field"
          label={<RequiredFieldLabel label="Fail Criteria" />}
          stretch
          errorText={fieldValidationError.failCriteria}
          description={MARKDOWN_TEXT_WARNING}
          info={<Tooltip content={AuditTooltipMessages.FAIL_CRITERIA} />}
        >
          <Container>
            <TextEditor
              data-testid="fail-criteria-editor"
              placeholder="Enter fail criteria details..."
              value={formData.failCriteria}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  failCriteria: value,
                }))
              }
              onChangeMedia={(data) =>
                setFileList((prev) => ({ ...prev, failCriteria: data }))
              }
              onFilesChange={(data) => {
                setDocumentFiles?.((prev) => ({ ...prev, failCriteria: data.files }));
                setDocumentFilesMetadata?.((prev) => ({
                  ...prev,
                  failCriteria: data.metadata,
                }));
              }}
              showFileUpload={enableFileUploads}
              initialFileMetadata={documentFilesMetadata.failCriteria || []}
            />
          </Container>
        </FormField>

        <FormField
          data-testid="justifications-field"
          errorText={fieldValidationError.justification}
          description={MARKDOWN_TEXT_WARNING}
          label={<RequiredFieldLabel label="Justifications" />}
          stretch
          info={<Tooltip content={AuditTooltipMessages.JUSTIFICATION} />}
        >
          <Container>
            <TextEditor
              data-testid="justifications-editor"
              placeholder="Enter justification details..."
              value={formData.justification}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  justification: value,
                }))
              }
              onChangeMedia={(data) =>
                setFileList((prev) => ({ ...prev, justification: data }))
              }
              onFilesChange={(data) => {
                setDocumentFiles?.((prev) => ({ ...prev, justification: data.files }));
                setDocumentFilesMetadata?.((prev) => ({
                  ...prev,
                  justification: data.metadata,
                }));
              }}
              showFileUpload={enableFileUploads}
              initialFileMetadata={documentFilesMetadata.justification || []}
            />
          </Container>
        </FormField>
        <Container
          data-testid="task-details-container"
          header={<Header variant="h2">Task Details (Optional)</Header>}
        >
          <TaskForm
            data-testid="task-form"
            taskData={taskData}
            onTaskChange={(updates) => {
              setFormData((prev) => ({
                ...prev,
                ...updates,
              }));
            }}
            onMediaChange={(data) =>
              setFileList((prev) => ({ ...prev, taskDescription: data }))
            }
            onFilesChange={(data) => {
              setDocumentFiles?.((prev) => ({ ...prev, taskDescription: data.files }));
              setDocumentFilesMetadata?.((prev) => ({
                ...prev,
                taskDescription: data.metadata,
              }));
            }}
            enableFileUploads={enableFileUploads}
            initialFileMetadata={documentFilesMetadata.taskDescription || []}
            labels={{
              nameLabel: 'Task Name',
              descriptionLabel: 'Task Description',
            }}
          />
        </Container>
      </SpaceBetween>
    </div>
  );
};

export default AuditCriteria;
